import * as d3 from 'd3';
import { binColor, binOpacity, type MonitorPalette } from '../../chart/palette';
import { MIN_CELL_PERMILLE, type G, type Model, type Point } from '../model';
import {
    modalTones,
    toneGradientStops,
    toneRuns,
    type ToneRun,
} from './decisionTone';

const TONE_GRADIENT_ID = 'monitor-pipr-tone';

const PIPR_WIDTH = 2;
const CASING_WIDTH = 3;
const CASING_OPACITY = 0.8;

const ANCHOR_WIDTH = 1.5;
const ANCHOR_OPACITY = 0.7;
const ANCHOR_DASH = '5 1';
const ANCHOR_CASING_WIDTH = 3;

const DECIDED_WIDTH = 1.5;
const DECIDED_OPACITY = 0.9;
const DECIDED_DASH = '2 3';
const DECIDED_CASING_WIDTH = 3;

export function drawHistoryCells(g: G, model: Model): void {
    if (model.layout.isNarrow) return;

    const cells: Array<{ x: number; y: number; c: string; o: number }> = [];
    model.vis.forEach(d => {
        d.p.forEach((q, j) => {
            if (q < MIN_CELL_PERMILLE) return;
            cells.push({
                x: model.x(d.date),
                y: model.yCellHist(d, j) - model.cellH / 2,
                c: binColor(model.deltas[j]),
                o: binOpacity(q),
            });
        });
    });

    g.selectAll('rect')
        .data(cells)
        .join('rect')
        .attr('x', c => c.x)
        .attr('y', c => c.y)
        .attr('width', model.colW)
        .attr('height', Math.max(2, model.cellH))
        .attr('rx', 1)
        .attr('fill', c => c.c)
        .attr('opacity', c => c.o);
}

function casing(
    g: G,
    d: string,
    width: number,
    pal: MonitorPalette,
    dash?: string
) {
    const path = g
        .append('path')
        .attr('fill', 'none')
        .attr('stroke', pal.card)
        .attr('stroke-opacity', CASING_OPACITY)
        .attr('stroke-width', width)
        .attr('d', d);
    if (dash) path.attr('stroke-dasharray', dash);
    else path.attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');
    return path;
}

function piprStroke(g: G, model: Model, pal: MonitorPalette): string {
    const runColor = (run: ToneRun) =>
        run.tone === 'hold' ? pal.ink : binColor(run.delta);
    const runs = toneRuns(
        modalTones(
            model.deltas,
            model.vis.map(d => d.p)
        )
    );
    const xAt = (i: number) => model.x(model.vis[i].date);
    const stops = toneGradientStops(runs, xAt, runColor);

    if (stops.length === 0) return pal.ink;
    if (runs.length === 1) return runColor(runs[0]);

    const grad = g
        .append('defs')
        .append('linearGradient')
        .attr('id', TONE_GRADIENT_ID)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', xAt(0))
        .attr('x2', xAt(model.vis.length - 1))
        .attr('y1', 0)
        .attr('y2', 0);

    grad.selectAll('stop')
        .data(stops)
        .join('stop')
        .attr('offset', s => s.offset)
        .attr('stop-color', s => s.color);

    return `url(#${TONE_GRADIENT_ID})`;
}

function drawPending(g: G, model: Model, pal: MonitorPalette): void {
    const open = (d: Point) => d.pending !== 0;

    const dBand = d3
        .area<Point>()
        .defined(open)
        .x(d => model.x(d.date))
        .y0(d => model.yAnchor(d))
        .y1(d => model.yDecided(d))
        .curve(d3.curveStepAfter)(model.vis);

    const dDecided = d3
        .line<Point>()
        .defined(open)
        .x(d => model.x(d.date))
        .y(d => model.yDecided(d))
        .curve(d3.curveStepAfter)(model.vis);

    if (dBand) {
        g.append('path')
            .attr('fill', pal.pendingBand)
            .attr('stroke', 'none')
            .attr('d', dBand);
    }

    if (!dDecided) return;

    casing(g, dDecided, DECIDED_CASING_WIDTH, pal, DECIDED_DASH);

    g.append('path')
        .attr('fill', 'none')
        .attr('stroke', pal.ink)
        .attr('stroke-opacity', DECIDED_OPACITY)
        .attr('stroke-width', DECIDED_WIDTH)
        .attr('stroke-dasharray', DECIDED_DASH)
        .attr('d', dDecided);
}

export function drawHistoryLines(
    g: G,
    model: Model,
    pal: MonitorPalette
): void {
    if (model.vis.length === 0) return;

    const dAnchor = d3
        .line<Point>()
        .x(d => model.x(d.date))
        .y(d => model.yAnchor(d))
        .curve(d3.curveStepAfter)(model.vis) as string;

    const dPipr = d3
        .line<Point>()
        .x(d => model.x(d.date))
        .y(d => model.yPipr(d))(model.vis) as string;

    if (model.hasPending) drawPending(g, model, pal);

    casing(g, dAnchor, ANCHOR_CASING_WIDTH, pal, ANCHOR_DASH);

    g.append('path')
        .attr('fill', 'none')
        .attr('stroke', pal.ink)
        .attr('stroke-opacity', ANCHOR_OPACITY)
        .attr('stroke-width', ANCHOR_WIDTH)
        .attr('stroke-dasharray', ANCHOR_DASH)
        .attr('d', dAnchor);

    casing(g, dPipr, CASING_WIDTH, pal);

    g.append('path')
        .attr('fill', 'none')
        .attr('stroke', piprStroke(g, model, pal))
        .attr('stroke-width', PIPR_WIDTH)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', dPipr);
}
